/**
 * PATH: packages/ui/src/kanban/components/KanbanCardFooter.tsx
 * FILE_ID: UI-KANBAN-CARD-FOOTER-001
 * PURPOSE: Card triage footer frame — signals, then icons + chrome.
 * ROLE IN ARCHITECTURE: Thin orchestrator over footer leaves.
 * MAIN EXPORTS: KanbanCardFooter, KanbanCardFooterProps
 * NON-RESPONSIBILITIES: Does not persist face prefs.
 * NOTES FOR FUTURE AI: Hide omits chrome. attachment_count derivation lives
 *   in kanbanCardFooterSignals.ts; the paperclip badge lives in Icons.
 */
import type { ReactNode } from 'react'

import type { KanbanEmailCardMeta, KanbanMember } from '../api'
import type { Card as KanbanCardModel } from '../types'
import { KanbanCardFooterChrome } from './KanbanCardFooterChrome'
import { KanbanCardFooterIcons } from './KanbanCardFooterIcons'
import {
  readFooterSignals,
  type FooterFieldVisibility,
} from './kanbanCardFooterSignals'

export interface KanbanCardFooterProps {
  card: KanbanCardModel
  isCompact: boolean
  emailMeta: KanbanEmailCardMeta | undefined
  assignedMembers: KanbanMember[]
  checklistDone: number
  checklistTotal: number
  commentCount: number
  overdue: boolean
  hovered: boolean
  checklistApiReady: boolean
  checklistExpanded: boolean
  onToggleChecklist: () => void
  onOpenEmail?: (card: KanbanCardModel) => void
  onOpenWorkbook?: (card: KanbanCardModel) => void
  relatedCount?: number
  unseenCount?: number
  hideActionPill?: boolean
  onSetActionDate?: (
    card: KanbanCardModel,
    actionDate: string | null,
  ) => Promise<unknown> | void
  onSetDueDate?: (
    card: KanbanCardModel,
    dueDate: string | null,
  ) => Promise<unknown> | void
  showDue?: boolean
  trailingSlot?: ReactNode
  fields?: FooterFieldVisibility
}

export function KanbanCardFooter({
  card,
  isCompact,
  emailMeta,
  assignedMembers,
  checklistDone,
  checklistTotal,
  commentCount,
  overdue,
  hovered,
  checklistApiReady,
  checklistExpanded,
  onToggleChecklist,
  onOpenEmail,
  onOpenWorkbook,
  relatedCount = 0,
  unseenCount = 0,
  hideActionPill = false,
  onSetActionDate,
  onSetDueDate,
  showDue = true,
  trailingSlot,
  fields,
}: KanbanCardFooterProps) {
  const signals = readFooterSignals({
    card,
    isCompact,
    emailMeta,
    assignedMembers,
    checklistTotal,
    commentCount,
    relatedCount,
    unseenCount,
    trailingSlot,
    fields,
  })
  if (!signals.shouldRender) return null

  return (
    <div
      className="mt-1.5 flex items-center justify-between gap-1.5 text-[10.5px] text-neutral-500 dark:text-zinc-400"
      style={{ color: 'var(--card-fg-muted)' }}
    >
      <KanbanCardFooterIcons
        card={card}
        isCompact={isCompact}
        checklistDone={checklistDone}
        checklistTotal={checklistTotal}
        commentCount={commentCount}
        overdue={overdue}
        hovered={hovered}
        checklistApiReady={checklistApiReady}
        checklistExpanded={checklistExpanded}
        onToggleChecklist={onToggleChecklist}
        onOpenEmail={onOpenEmail}
        onOpenWorkbook={onOpenWorkbook}
        relatedCount={relatedCount}
        unseenCount={unseenCount}
        hideActionPill={hideActionPill}
        onSetActionDate={onSetActionDate}
        onSetDueDate={onSetDueDate}
        showDue={showDue}
        fields={fields}
        signals={signals}
      />
      <KanbanCardFooterChrome
        card={card}
        isCompact={isCompact}
        emailMeta={emailMeta}
        assignedMembers={assignedMembers}
        trailingSlot={trailingSlot}
        fields={fields}
      />
    </div>
  )
}
